import React from 'react';
import {
  useEffectiveRoleViewAuthorizationServiceContext,
  useMetricDetailsMapContext,
} from '../../../../context/contexts';
import { MetricIdType, MetricIdTypeArrays } from '../../../../types';
import { MetricQueryTooltip } from '../../modals/MetricQueryModal';
import { TitleConfig } from '../../ui-components/data-viz-ui/charts/common/common-chart-types';
import { useMultipleMetricsQuery } from '../common/hooks';
import { DataOrError, ExternalChartComponentProps } from '../common/types';
import {
  MetricTableCellValue,
  MetricTableRowData,
  MetricTableVizConfig,
  MetricTableVizData,
  MetricTableVizDataResponse,
} from './types';

/**
 * Generates the title config for the metric table.
 */
const useChartTitle = (
  config: MetricTableVizConfig,
  externalProps: ExternalChartComponentProps,
  allMetricsSql: (string | null)[]
): TitleConfig => {
  const { locale } = externalProps;
  const titleFromConfig = config.title?.[locale.selected];
  const authorizationService =
    useEffectiveRoleViewAuthorizationServiceContext();
  const canSeeChartDevOptions = authorizationService.canSeeChartDevOptions();

  // Collect all non-null metrics for the tooltip
  const allMetrics = config.rows
    .flatMap((row) => row.cells)
    .filter((cell): cell is MetricIdType => cell !== null);

  const extraContent = canSeeChartDevOptions ? (
    <MetricQueryTooltip
      metrics={allMetrics as MetricIdTypeArrays}
      metricsSql={allMetricsSql}
    />
  ) : undefined;

  return {
    mainTitle: titleFromConfig ?? '',
    extraContent,
  };
};

/**
 * Fetches data for all metrics in the table and transforms it into the response format.
 * Returns raw values without formatting - formatting is done in dataToUiComponentProps.
 */
const useMetricTableResponse = (
  config: MetricTableVizConfig,
  externalProps: ExternalChartComponentProps
): DataOrError<MetricTableVizDataResponse> => {
  const { timeSliderState, segmentationState, filtersState, locale } =
    externalProps;
  const { rows, timeSelection, filters } = config;

  // Collect all non-null metrics from all cells
  const allMetrics: (MetricIdType | null)[] = rows.flatMap((row) => row.cells);
  const nonNullMetrics = allMetrics.filter(
    (m): m is MetricIdType => m !== null
  ) as MetricIdTypeArrays;

  const { queryResponse } = useMultipleMetricsQuery({
    metrics: nonNullMetrics,
    timeSelection,
    timeSliderState,
    segmentationState,
    filters,
    filtersState,
  });

  // Check for any errors
  if (queryResponse.error) {
    return { data: null, error: queryResponse.error };
  }

  // Build a map from metric value to its result
  const metricResultMap = new Map<
    string,
    { value: number; metricSql: string | null }
  >();
  nonNullMetrics.forEach((metric, index) => {
    const metricResult = queryResponse.data[index];
    const value =
      (metricResult.segments[0]?.groupSegments[0]?.data as number) ?? 0;
    metricResultMap.set(String(metric.value), {
      value,
      metricSql: metricResult.meta.metricSql,
    });
  });

  // Transform into row data with raw values (no formatting)
  const rowsData: MetricTableRowData[] = rows.map((row) => {
    const label = row.label[locale.selected] ?? '';
    const cells: MetricTableCellValue[] = row.cells.map((cellMetric) => {
      if (cellMetric === null) {
        return {
          metric: null,
          value: null,
          metricSql: null,
        };
      }

      const result = metricResultMap.get(String(cellMetric.value));

      if (!result) {
        return {
          metric: cellMetric,
          value: null,
          metricSql: null,
        };
      }

      return {
        metric: cellMetric,
        value: result.value,
        metricSql: result.metricSql,
      };
    });

    return { label, cells };
  });

  return {
    data: { rows: rowsData },
    error: null,
  };
};

/**
 * Hook to convert MetricTableVizConfig to data for visualization.
 * Fetches all metrics in parallel and returns formatted table data.
 */
export const useMetricTableConfigToData = (
  config: MetricTableVizConfig,
  externalProps: ExternalChartComponentProps
): DataOrError<MetricTableVizData> => {
  const metricDetailsMap = useMetricDetailsMapContext();
  const { locale } = externalProps;

  const { data, error } = useMetricTableResponse(config, externalProps);
  // Collect all SQL for the tooltip
  const allMetricsSql =
    data?.rows.flatMap((row) =>
      row.cells.map((cell) => cell.metricSql).filter((sql) => sql !== null)
    ) ?? [];

  const titleConfig = useChartTitle(config, externalProps, allMetricsSql);

  // Transform headers to localized strings
  const headers = config.headers.map((h) => h[locale.selected] ?? '');

  if (error) {
    return { data: null, error };
  }

  return {
    data: {
      dataResponse: data,
      titleConfig,
      headers,
      metricDetails: metricDetailsMap,
    },
    error: null,
  };
};
